import React, { useEffect } from 'react';
import Heading from '../../../components/typography';
import { COLORS } from '../../../constants/style/color';
import InitialValuesForm from '../../../constants/initialValues';
import {
  useGetBrokerAttestationAuditLogs,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import {
  TermsAndConditionsService,
} from '@wealthlane/api-services/wealthlane-customer-service';
import { handleOpenFile } from '../../../utils/global';
import {
  SettingSystemUserHeader,
  SystemUserContainer,
} from '../system-users/systemUserStyled';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import Spinners from '../../../components/spinner';
import { axiosInstance } from '../../../services/apiInterceptor';
import { BrokerAttestationAuditLogHeader } from './brokerAttestationAuditLogHeader';

const BrokerAttestationAuditLogs = (): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const { tableInfo, setTableInfo } = useDynamicTable();

  useEffect(() => {
    setTableInfo(InitialValuesForm.tableParams);
  }, []);

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'BROKER_ATTESTATION_AUDIT_LOGS_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });

  const { data, status } = useGetBrokerAttestationAuditLogs(handleRequest, tableInfo);

  const handleViewTnC = (id: string, rowData: any): void => {
    const termsAndConditionsId = id || rowData?.termsAndConditionsId;
    if (!termsAndConditionsId) return;
    handleRequest(
      TermsAndConditionsService.postApiCustomerApiAppTermsAndConditionsDownloadDocument(
        termsAndConditionsId
      )
    )
      .then((response: any) => {
        if (response != null) {
          handleOpenFile(response.name, response.content);
        }
      })
      .catch((error: any) => {
        console.error('Failed to download T&C document:', error);
      });
  };

  const tableActions = [
    {
      action: 'View T&C',
      onClick: handleViewTnC,
    },
  ];

  const schema = React.useMemo(
    () => ({
      columns: BrokerAttestationAuditLogHeader({
        actions: schemaData?.columns,
        tableActions,
      }),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );

  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  return (
    <SystemUserContainer id='pageContainer' data-page='brokerAttestationAuditLogs'>
      <SettingSystemUserHeader id='pageHeader'>
        <Heading variant='h3' title='Attestation Audit Logs' id='pageTitle' />
        <Heading
          variant='body-s'
          title='Audit log of participant broker attestation acknowledgements.'
          color={COLORS.Gray}
          id='pageSubtitle'
        />
      </SettingSystemUserHeader>

      <DynamicTable
        schema={schema}
        data={data?.items ?? []}
        totalCounts={data?.totalCount ?? 0}
        loading={status}
        id='brokerAttestationAuditLogsTable'
      />
    </SystemUserContainer>
  );
};

export default withDynamicTableContext(BrokerAttestationAuditLogs, false, axiosInstance);
